import AppAvatar from '@app/components/ui/Avatar';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiPackage, FiRepeat } from 'react-icons/fi';
import { VisitAssignmentBatchAddedNotificationType } from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';

type AssignmentBatchWidgetNotificationCardProps = {
  notificationDetails: VisitAssignmentBatchAddedNotificationType;
  onClick: (id: number) => void;
};

/** The widget's compact form of {@link AssignmentBatchNotificationCard}: the
 * count is kept because it is the one thing that distinguishes this from an
 * ordinary assignment; the span is left to the full card. */
const AssignmentBatchWidgetNotificationCard = ({
  notificationDetails,
  onClick,
}: AssignmentBatchWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];
  const timeAgo = (iso: string) => dayjs(iso).fromNow();

  const caregiverFullname = meta?.caregiverNames?.[0];
  const fullnameLetters = getInitials(caregiverFullname || '', { fallback: '' });

  const isPackage = meta?.batchKind === 'package';
  const isRecurring = meta?.batchKind === 'recurring';
  const icon = isPackage ? <FiPackage size={18} /> : isRecurring ? <FiRepeat size={18} /> : <FiCalendar size={18} />;

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className={`group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer`}>
        <div
          onClick={() => onClick(notificationDetails.notificationId)}
          className="flex w-full items-start gap-3 text-left"
        >
          {caregiverFullname ? (
            <AppAvatar isSelected={true} fullName={caregiverFullname} value={fullnameLetters} />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
              {icon}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700 line-clamp-2">{notificationDetails.title}</h3>

                {typeof meta?.visitCount === 'number' && (
                  <Tag bordered={false} color="geekblue">
                    <span className="font-light">
                      {t('notifications-assignment-visit-count', { count: meta.visitCount })}
                    </span>
                  </Tag>
                )}
              </div>

              <span className="ml-2 shrink-0 text-xs text-slate-400">{timeAgo(notificationDetails.createdAt)}</span>
            </div>

            <p className="mt-0.5 line-clamp-4 text-xs text-slate-600">{notificationDetails.message}</p>
          </div>

          {!notificationDetails.isRead && <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />}
        </div>
      </div>
    </motion.li>
  );
};

export default AssignmentBatchWidgetNotificationCard;
